import React, { useEffect, useState, useMemo, useRef } from "react";
import clsx from "clsx";
import { orderBy } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
    Box,
    Button,
    Grid,
    Avatar,
    Typography,
    IconButton,
    Tooltip
} from "@mui/material/";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { BrokenImage } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Document, Page, pdfjs } from "react-pdf";
import { helpers } from "../../../../common/lib";
import actions from "../../../../Store/actions";
import AuditImageDetail from "./AuditImageDetail";
import ControllerUploads from "../ControllerUploads";
import styles from "./styles";
import ImageControls from "../../../../common/components/ImageControls";
import FoundElseWhere from "./FoundElseWhere";
import Thumbnail from "./Thumbnail";
import ImageViewer from "./ImageViewer";
import PdfViewer from "./PdfViewer";
import { logToSentry } from "../../../../config/Sentry";
import { strings } from "../../../../common/i18n";

// Use a pdfjs-dist v3 CDN worker which is compatible with Node 18 projects

export default ({
    identifiers = {},
    foundElsewhere,
    value,
    altAssetId,
    altAssetImageId,
    assetAuditorId,
    assetAuditorImageId,
    uploadData,
    topBanner
}) => {
    const theme = useTheme();
    const classes = makeStyles(styles)();

    const { assetAuditors, altAssets, auditControllers } = useSelector(
        (state) => state
    );
    const [index, setIndex] = useState(0);
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(800);
    const viewerRef = useRef(null);
    const dispatch = useDispatch();

    /** Current asset in controller */
    const auditAssetController = useMemo(
        () =>
            auditControllers.data.find(
                (item) => item.auditAsset === parseInt(assetAuditorId)
            ),
        [value, auditControllers]
    );

    const data = useMemo(() => {
        let img = [];
        /** If the asset found elsewhere is true
         * Controller uploads the image
         */
        if (auditAssetController && foundElsewhere) {
            img =
                auditAssetController.auditImages.length > 0
                    ? [...auditAssetController.auditImages, ...value]
                    : value;
        } else img = value;

        if (img && img.length) {
            return orderBy(img, ["createdDate"], ["asc"]);
        }
        return [];
    }, [value, auditAssetController]);

    useEffect(() => {
        // setIndex(0);
    }, [data, altAssetImageId, assetAuditorImageId]);

    useEffect(() => {
        const updateWidth = () => {
            // measure the viewer container and set page width so PDF fits nicely
            if (viewerRef.current) {
                // subtract a small gutter so the page doesn't touch edges
                const w = Math.max(300, viewerRef.current.clientWidth - 16);
                setPageWidth(w);
            }
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [data, index]);

    const handleThumbClick = (next) => () => setIndex(next);

    const onRotation = async (angle) => {
        try {
            const { path, ...filteredData } = data[index];
            const res = await dispatch(
                actions.auditImages.create.patchRequest({
                    ...filteredData,
                    ...angle
                })
            );

            if (assetAuditorImageId) {
                const assetAuditor = helpers.getListItemForId(
                    assetAuditors,
                    assetAuditorImageId
                );
                const index = assetAuditor.auditImages.findIndex(
                    (item) => item.id === res.auditImage.id
                );

                if (index === -1) {
                    const asset = auditControllers.data.find(
                        (item) => item.auditAsset === assetAuditorId
                    );

                    asset.auditImages.find((item) => {
                        if (item.id === res.auditImage.id) {
                            item.rotation = res.auditImage.rotation;
                        }
                    });
                    await dispatch(
                        actions.auditControllers.create.merge(auditControllers)
                    );
                } else {
                    assetAuditor.auditImages[index] = {
                        ...assetAuditor.auditImages[index],
                        ...res.auditImage
                    };
                    dispatch(
                        actions.assetAuditors.create.merge([assetAuditor])
                    );
                }
            } else if (altAssetImageId) {
                const altAsset = helpers.getListItemForId(
                    altAssets,
                    altAssetImageId
                );

                const index = altAsset.altAssetImages.findIndex(
                    (item) => item.image.id === res.auditImage.id
                );

                altAsset.altAssetImages[index].image = {
                    ...altAsset.altAssetImages[index].image,
                    ...res.auditImage
                };
                dispatch(actions.altAssets.create.merge([altAsset]));
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On rotation:", { error });
            dispatch(actions.toast.create.error("Error rotating image"));
        }
    };

    const checkAvatarType = (item) => {
        let userType;

        if (item.hasOwnProperty("assetAuditor") && !item.isSharedAsset) {
            userType = "A";
        } else if (item.isSharedAsset) {
            userType = "V";
        } else {
            userType = "C";
        }

        if (altAssetId) {
            return <Avatar className={classes.avatarIdentifierSmall}>A</Avatar>;
        } else {
            return (
                <Avatar className={classes.avatarIdentifierSmall}>
                    {userType}
                </Avatar>
            );
        }
    };

    const isPdf = (path) => {
        if (!path) return false;
        const cleanPath = path.split("?")[0].toLowerCase(); // remove query params
        return cleanPath.endsWith(".pdf");
    };

    const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

    const openPdfInNewWindow = (pdfUrl) => {
        const width = 1200;
        const height = 800;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const googleViewer = `https://docs.google.com/viewer?url=${encodeURIComponent(
            pdfUrl
        )}&embedded=true`;

        window.open(
            googleViewer,
            "_blank",
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );
    };

    return (
        <>
            {data[index] && data[index].foundElsewhere && (
                <FoundElseWhere foundElsewhere={foundElsewhere} />
            )}

            {data[index] && (
                <Box sx={styles(theme).fullHeight}>
                    <Box sx={styles(theme).auditImage} ref={viewerRef}>
                        {isPdf(data[index].path) ? (
                            <>
                                <PdfViewer
                                    pdfKey={`${data[index].createdDate}-pdf`}
                                    path={data[index].path}
                                    pageWidth={pageWidth}
                                    numPages={numPages}
                                    setNumPages={setNumPages}
                                />
                            </>
                        ) : (
                            <>
                                <ImageViewer
                                    item={data[index]}
                                    pageWidth={pageWidth}
                                    identifiers={identifiers}
                                    topBanner={topBanner}
                                    assetAuditorImageId={assetAuditorImageId}
                                    altAssetImageId={altAssetImageId}
                                    assetAuditorId={assetAuditorId}
                                    altAssetId={altAssetId}
                                    assetAuditors={assetAuditors}
                                    altAssets={altAssets}
                                    auditControllers={auditControllers}
                                />
                            </>
                        )}
                    </Box>

                    <Box className={classes.auditImageDetailContainer}>
                        <AuditImageDetail
                            foundElsewhere={foundElsewhere}
                            auditImage={data[index] || null}
                        />
                    </Box>

                    <Grid
                        container
                        className={classes.auditImageThumbContainer}
                    >
                        {data.length > 1 && (
                            <Grid className={classes.auditImageThumbs}>
                                {data.map((item, index) => (
                                    <Thumbnail
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        selectedIndex={index}
                                        onClick={handleThumbClick}
                                        checkAvatarType={checkAvatarType}
                                        isPdf={isPdf}
                                    />
                                ))}
                            </Grid>
                        )}

                        {altAssetImageId === undefined ? (
                            <ControllerUploads uploadData={uploadData} />
                        ) : null}
                    </Grid>
                </Box>
            )}
        </>
    );
};
